import { DateTime } from 'luxon';
/**
 * Get a DateTime object of the current date-time (in UTC).
 */
export const getNow = DateTime.utc;
/**
 * Sorts an array of dates.
 *
 * @param dates - list of dates
 */
export const sortDates = (dates) => {
    return dates.sort((dt1, dt2) => dt1.toMillis() - dt2.toMillis());
};
/**
 * @param {DateTime}
 * @returns {boolean}
 */
const isFriday = (date) => date.weekday === 5;
/**
 * @param {DateTime} date
 * @returns {boolean}
 */
const isSaturday = (date) => date.weekday === 6;
/**
 * @param {DateTime} date
 * @returns {boolean}
 */
const isSunday = (date) => date.weekday === 7;
/**
 * **Note**: For this function to work the datetime needs to be localized. Example:
 * `DateTime.fromISO('2022-07-11T00:00:00.000', {zone: 'Europe/Moscow'})`
 *
 * @param {DateTime} date
 * @returns {boolean}
 */
export const isWeekendDay = (date) => {
    return isSaturday(date) || isSunday(date);
};
/**
 * @param {DateTime} date
 * @returns {boolean}
 */
export const isFridayOrSaturday = (date) => {
    return isFriday(date) || isSaturday(date);
};
/**
 * Allows us to check if a date is at least X hours ahead of another reference date.
 * @param date
 * @param referenceDate
 * @param hoursThreshold
 * @returns fails if the date is NOT at least hoursThreshold from the referenceDate.
 */
// prettier-ignore
export const dateIsMinHoursAheadOfReferenceDate = (date, referenceDate, hoursThreshold = 24) => {
    return date.toMillis() >= referenceDate.plus({ hours: hoursThreshold }).toMillis();
};
/**
 * Date is at least the following calendar X weeks after the reference date. Where 1 means the following week.
 *
 * @returns `true` if the date is the following calendar week from the reference date.
 *
 * **Note**: One known edge case is when new-year falls midweek and the selected dates are 31st and 2nd.
 * Then it will pass on the year check.
 */
// prettier-ignore
export const dateIsWeeksAfterReferenceDate = (date, referenceDate, weeksOffset = 1) => {
    return date.year > referenceDate.year || date.weekNumber - referenceDate.weekNumber >= weeksOffset;
};
/**
 * Convert the provided date-time (in UTC) to the same date-time in the provided time-zone.
 * @param datetime
 * @param zone
 */
export function adjustUTCtoTimezone(datetime, zone) {
    if (datetime instanceof Date)
        datetime = datetime.toISOString();
    return DateTime.fromISO(datetime, { zone });
}
;
/**
 * Shift the provided date (or "now") by the provided amount.
 * @param originalDate
 * @param timezoneIso The timezone of the original date (which is stored in broken UTC)
 * @param shiftBy
 * @param now
 */
export function shiftScheduleDate(originalDate, timezoneIso, { amount, unit }, now = getNow()) {
    const ogDT = adjustUTCtoTimezone(originalDate, timezoneIso);
    const nowLocal = now.setZone(timezoneIso);
    const originalDayOfWeek = ogDT.weekday;
    const dtToShift = ogDT < nowLocal
        ? nowLocal.set({
            hour: ogDT.hour,
            minute: ogDT.minute,
            second: 0,
            weekday: originalDayOfWeek
        })
        : ogDT.set({ second: 0 });
    return dtToShift.plus({ [unit]: amount });
}
/**
 * Shift the provided date by an amount calculated as the difference/duration/delta between
 * two reference dates.
 *
 * @param dateToShift - The date we want to shift
 * @param relativeToDateBefore - The 'before' date we want to use to calculate the duration (timedelta)
 * @param relativeToDateAfter - The 'after' date we want to use to calculate the duration (timedelta)
 * @returns
 */
export function shiftRelativeToDates(dateToShift, relativeToDateBefore, relativeToDateAfter) {
    const timeDeltaSeconds = relativeToDateAfter
        .diff(relativeToDateBefore, ["years", "months", "days", "hours", "minutes"]).as('seconds');
    return dateToShift.plus({ seconds: timeDeltaSeconds });
}
